<!-- src/pages/MyPage.vue -->
<script setup>
import { onMounted, onUnmounted, computed } from 'vue';
definePage({ meta: { layout: 'mypage' } });
import { paymentOwn, ownPoint, historyPoint, historyAnalysisTicket } from '@/api/PaymentApi';
import { addContentLogAPI } from '@/api/logServiceApi';
import { formatCustomDate } from '@/composables/functions';
import { useRoute } from 'vue-router';
import { DEV_MODE } from '@/config/dev-mode';
import { usePaymentsStore } from '@/store/modules/payments';

import IconService01 from '@/components/svg/IconService01.vue';
import IconService02 from '@/components/svg/IconService02.vue';
import IconService03 from '@/components/svg/IconService03.vue';
import IconService04 from '@/components/svg/IconService04.vue';
import IconService05 from '@/components/svg/IconService05.vue';
import IconService06 from '@/components/svg/IconService06.vue';
import IconSmile from '@/components/svg/IconSmile.vue';
import IconHeaderBack from '@/components/svg/IconHeaderBack.vue';
import MobileHeader from '@/components/common/MobileHeader.vue';
import { Pagination } from '@/components/board';
import subscrList from '@/components/mypage/subscrList.vue';
import analysisTicketList from '@/components/mypage/analysisTicketList.vue';
import AnalysisTicketDetailLayer from '@/components/mypage/AnalysisTicketDetailLayer.vue';

import Snackbar from '@/components/common/Snackbar.vue';
import { useSnackbar } from '@/composables/useSnackbar';
import { useDeviceDetect } from '@/composables/useDeviceDetect';
import { useBackLayer } from '@/composables/useAppBack';

const router = useRouter();
const route = useRoute();

const paymentsStore = usePaymentsStore();

const { snackbarRef, triggerSnackbar } = useSnackbar();
const { isMobile } = useDeviceDetect();

// ========================= 모바일 앱과 통신 =========================
const isWebView = computed(() => typeof window !== 'undefined' && !!window.ReactNativeWebView);

/** 뒤로가기: 항상 메인으로 */
const handleBack = () => {
  router.replace('/main');
};

/** 포인트 이용 내역 탭 클릭 로그 (클릭 시에만 발화 — payList는 로드/새로고침 시에도 호출되므로 분리) */
const onPointUseTabClick = () => {
  addContentLogAPI('MY_point_history_btn');
  payList('POINT_USE');
};

const payOwnList = ref(null);
const isListLoading = ref(false);
// POINT: 전체 이용내역, POINT_CHARGE: 충전내역, POINT_USE: 포인트 사용내역, POINT_TICKET: 분석권
const tabType = ref('POINT_CHARGE');

const currentPoints = ref(0);

let totalElements = ref(0);
let totalPages = ref(0);
const totalCount = ref(0);

// 사용 가능한 분석권 총 개수 (헤더의 분석권 건수와 동일하게)
const availableTicketCount = computed(() => {
  if (tabType.value !== 'POINT_TICKET') return 0;
  // 헤더에 표시되는 분석권 건수와 동일하게 (paymentsStore.analysisTicketCount 사용)
  return paymentsStore.analysisTicketCount || 0;
});

// 분석권 정렬 옵션
const ticketSortOption = ref('all'); // 'all': 전체, 'unused': 미사용보기, 'expiry': 유효기간 순
const isDropdownOpen = ref(false);

// 선택된 정렬 옵션 레이블
const ticketSortLabel = computed(() => {
  const labels = {
    all: '구매 순',
    unused: '미사용보기',
    expiry: '유효기간 순',
  };
  return labels[ticketSortOption.value] || '구매 순';
});

// 분석권 상세 레이어
const showTicketDetailLayer = ref(false);
const selectedTicketId = ref(null);
const selectedTicketIds = ref(null);
const layerPosition = ref({ top: 0, left: 0, width: 0 });
const layerDirection = ref('below'); // 'below' or 'above'

// 분석권 아이템 클릭 핸들러 (토글 기능 포함)
const handleTicketItemClick = (data) => {
  if (typeof data === 'number') {
    // 기존 방식 호환성 유지
    // 같은 티켓을 클릭하면 닫기
    if (showTicketDetailLayer.value && selectedTicketId.value === data) {
      handleCloseTicketDetailLayer();
      return;
    }
    selectedTicketId.value = data;
    selectedTicketIds.value = null;
    showTicketDetailLayer.value = true;
  } else {
    // 새로운 방식: 위치 정보 포함
    const newTicketId = data.analysisTicketId || null;
    const newTicketIds = data.analysisTicketIds || null;

    // 같은 티켓을 클릭하면 닫기 (토글)
    const isSameTicket =
      (newTicketId && selectedTicketId.value === newTicketId) ||
      (newTicketIds &&
        selectedTicketIds.value &&
        JSON.stringify(newTicketIds) === JSON.stringify(selectedTicketIds.value));

    if (showTicketDetailLayer.value && isSameTicket) {
      handleCloseTicketDetailLayer();
      return;
    }

    selectedTicketId.value = newTicketId;
    selectedTicketIds.value = newTicketIds;

    // 뷰포트 공간 계산: 위/아래 중 공간이 넓은 쪽으로 열기
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - (data.position.viewportBottom || 0);
    const spaceAbove = data.position.viewportTop || 0;

    if (spaceBelow >= spaceAbove) {
      layerDirection.value = 'below';
      layerPosition.value = { top: data.position.topBelow };
    } else {
      layerDirection.value = 'above';
      layerPosition.value = { top: data.position.topAbove };
    }

    showTicketDetailLayer.value = true;
  }
};

// 레이어 닫기
const handleCloseTicketDetailLayer = () => {
  showTicketDetailLayer.value = false;
  selectedTicketId.value = null;
  selectedTicketIds.value = null;
  layerPosition.value = { top: 0, left: 0, width: 0 };
  layerDirection.value = 'below';
};

// 외부 클릭 감지
const handleClickOutsideLayer = (event) => {
  if (showTicketDetailLayer.value) {
    const layer = event.target.closest('.ticket-detail-popup-content');
    const priceElement = event.target.closest('.price');
    if (!layer && !priceElement) {
      handleCloseTicketDetailLayer();
    }
  }
};

// 모바일 스크롤 시 레이어 닫기
const handleScrollCloseLayer = () => {
  if (isMobile.value && showTicketDetailLayer.value) {
    handleCloseTicketDetailLayer();
  }
};

onMounted(() => {
  // 포인트 이용내역 페이지 진입 로그 (신규 type, DB 등록 후 작동)
  addContentLogAPI('MY_point_history_view');
  document.addEventListener('click', handleClickOutsideLayer);
  window.addEventListener('scroll', handleScrollCloseLayer, true);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutsideLayer);
  window.removeEventListener('scroll', handleScrollCloseLayer, true);
});

// 정렬 옵션 텍스트 반환
const getSortOptionText = () => {
  if (ticketSortOption.value === 'unused') {
    return '미사용보기';
  } else if (ticketSortOption.value === 'expiry') {
    return '유효기간 순';
  }
  return '구매 순';
};

//검색조건
let paramData = ref({
  page: 1,
  size: 15,
});

//내포인트
//TODO: 보유 포인트
const getPoint = async () => {
  const params = {};
  const data = await ownPoint(params);
  currentPoints.value = data;
};

//TODO: 목록
const payList = async (tabTypeVal) => {
  // 탭이 변경되었을 때 페이지를 1로 리셋하고 이전 데이터 즉시 클리어
  if (tabType.value !== tabTypeVal) {
    paramData.value.page = 1;
    payOwnList.value = null;
  }

  tabType.value = tabTypeVal;
  isListLoading.value = true;
  let params = null;
  params = {
    page: paramData.value.page,
    size: 10,
  };
  const urlParams = new URLSearchParams(params);
  /*
  분석권 사용내역 API
  1. endpoint :  GET  /payments/own/analysis-ticket/history  회원의 분석권 이력 조회
  2. 분석권 데이터 : analysisTicket (object type)
  3. 보기선택 :
  isUnused=true  <미사용 내역만 보기>
  orderByExpiry=true <유효기간 순서대로 보기>
  usedStatus (UNUSED: 미사용 , USING: 사용중, USED: 완료)
  */

  if (tabTypeVal === 'ING') {
    urlParams.append('productType', 'SUBSCRIPTION');
    urlParams.append('productType', 'PLAN_TICKET');
    urlParams.append('isExpired', false);
  } else if (tabTypeVal === 'FINISH') {
    urlParams.append('productType', 'SUBSCRIPTION');
    urlParams.append('productType', 'PLAN_TICKET');
    urlParams.append('isExpired', true);
  } else if (tabTypeVal.startsWith('POINT')) {
    // 포인트 이용내역(전체) / 충전 내역 / 포인트 이용 내역 / 분석권 사용 내역 탭 공통
    // 충전내역 (pointEventType=CHARGE) - 백엔드 필터링
    // 포인트 이용 내역 (pointEventType=USE) - 백엔드 필터링
    // 분석권 사용 내역은 별도 API 사용

    if (tabTypeVal === 'POINT_CHARGE') {
      urlParams.append('pointEventType', 'CHARGE');
    } else if (tabTypeVal === 'POINT_USE') {
      urlParams.append('pointEventType', 'USE');
    }
    // POINT_TICKET은 별도 API 사용
    getPoint();
  }

  if (tabTypeVal === 'POINT_TICKET') {
    // 분석권 사용 내역은 별도 API 사용
    const ticketParams = {
      page: paramData.value.page,
      size: 10,
    };
    if (ticketSortOption.value === 'unused') {
      ticketParams.isUnused = true;
    }
    if (ticketSortOption.value === 'expiry') {
      ticketParams.orderByExpiry = true;
    }
    const data = await historyAnalysisTicket(ticketParams);
    payOwnList.value = data?.content || [];
    totalElements.value = data?.totalElements || 0;
    totalPages.value = data?.totalPages || 0;
  } else if (tabTypeVal.startsWith('POINT')) {
    const queryString = urlParams.toString();
    const data = await historyPoint(queryString);
    payOwnList.value = data?.content;
    totalElements.value = data.totalElements;
    totalPages.value = data.totalPages;
  } else {
    const queryString = urlParams.toString();
    const data = await paymentOwn(queryString);
    payOwnList.value = data?.content;
    totalElements.value = data.totalElements;
    totalPages.value = data.totalPages;
  }
  isListLoading.value = false;
};

onMounted(async () => {
  await paymentsStore.fetchTicketCount();
  await paymentsStore.getOwnPoint();

  if (route.query.page) {
    paramData.value.page = parseInt(route.query.page) || 1;
  }

  if (route.query.tabType) {
    tabType.value = route.query.tabType;
    payList(route.query.tabType);
  } else {
    payList('POINT_CHARGE');
  }
});

const goPayView = (orderId, productType) => {
  router.push(
    `/mypage/subscrPaymentView/${orderId}?tabType=${tabType.value}&productType=${productType}`,
  );
};

const goLink = (url) => {
  router.push(`${url}`);
};

// 숫자에 천 단위 구분 쉼표를 추가하는 함수
const formatNumber = (num) => {
  if (num < 1000) return num;
  return new Intl.NumberFormat().format(num);
};

const changePage = (page) => {
  paramData.value.page = page;
  payList(tabType.value);
  //store.dispatch('saveCurrentEssayPage', page);
};

const chargePoints = () => {
  const path = '/purchase/points';
  router.push(path);
};

// 분석권 구매 페이지로 이동
const goToTicketPurchase = () => {
  const path = '/purchase/tickets';
  router.push(path);
};

// 분석권 정렬 옵션 변경
const changeTicketSortOption = (option) => {
  ticketSortOption.value = option;
  isDropdownOpen.value = false;
  paramData.value.page = 1; // 페이지 리셋
  payList(tabType.value);
};

// 드롭다운 토글
const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

useBackLayer({
  id: 'mypage-history-ticket-sort',
  isOpen: isDropdownOpen,
  closeFromBack: () => {
    isDropdownOpen.value = false;
  },
});
useBackLayer({
  id: 'mypage-history-ticket-detail',
  isOpen: showTicketDetailLayer,
  closeFromBack: handleCloseTicketDetailLayer,
});

// 드롭다운 외부 클릭 시 닫기
const handleClickOutside = (event) => {
  const dropdown = event.target.closest('.ticket-sort-dropdown');
  if (!dropdown) {
    isDropdownOpen.value = false;
  }
};

// 외부 클릭 감지 (드롭다운용)
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('click', handleClickOutsideLayer);

  // 모바일 고정 포인트 footer가 사이트 footer를 가리지 않도록 여백 추가
  if (isMobile.value) {
    const layoutFooter = document.querySelector('.mypage-layout > footer');
    if (layoutFooter) layoutFooter.style.paddingBottom = '50px';
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('click', handleClickOutsideLayer);

  // 사이트 footer 여백 원복
  const layoutFooter = document.querySelector('.mypage-layout > footer');
  if (layoutFooter) layoutFooter.style.paddingBottom = '';
});
</script>

<template>
  <div class="my-info-wrap">
    <MobileHeader
      title="이용중인 서비스"
      :show-back="isWebView"
      :show-menu="!isWebView"
      @back="handleBack"
    />
    <div class="my-tit-box com-bg-my-box h-small">
      <div class="tit">
        이용중인 서비스
        <span class="tit-s">상세화면에서 결제 및 사용 내역과 영수증을 확인하실 수 있습니다.</span>
      </div>
      <div class="path">
        <ul>
          <li>홈</li>
          <li>마이페이지</li>
          <li>낭만 서비스</li>
          <li>이용중인 서비스</li>
        </ul>
      </div>
    </div>
    <div class="myinfo-box com-bg-my-box history">
      <!-- {{ payOwnList }} -->
      <div class="my-tab-st1-box">
        <div class="btn-box">
          <!-- <button class="tab-btn" :class="{ active: tabType === 'ING' }" @click="payList('ING')">
            이용중인 서비스
          </button>
          <button
            class="tab-btn"
            :class="{ active: tabType === 'FINISH' }"
            @click="payList('FINISH')"
          >
            만료된 서비스
          </button> -->
          <!-- <button
            class="tab-btn"
            :class="{ active: tabType === 'POINT' }"
            @click="payList('POINT')"
          >
            이용 내역(전체 임시 표시)
          </button> -->
          <button
            class="tab-btn"
            :class="{ active: tabType === 'POINT_CHARGE' }"
            @click="payList('POINT_CHARGE')"
          >
            충전 내역
          </button>
          <button
            class="tab-btn"
            :class="{ active: tabType === 'POINT_USE' }"
            @click="onPointUseTabClick"
          >
            포인트 이용 내역
          </button>
          <button
            class="tab-btn"
            :class="{ active: tabType === 'POINT_TICKET' }"
            @click="payList('POINT_TICKET')"
          >
            분석권 사용 내역
          </button>
        </div>
        <div
          class="point-txt-box"
          v-if="tabType && tabType.startsWith('POINT') && tabType !== 'POINT_TICKET'"
        >
          <span class="point-label">보유 낭만 포인트</span>
          <span class="point-num-box">
            <i class="icon-point"></i>
            <span class="point-value">{{ formatNumber(currentPoints) }} p</span>
          </span>
        </div>
        <!-- 분석권 사용 내역 정렬 선택 -->
        <div v-if="tabType === 'POINT_TICKET'" class="ticket-sort-wrapper">
          <!-- PC: 드롭다운 왼쪽, 카운트 오른쪽 -->
          <template v-if="!isMobile">
            <div class="ticket-sort-dropdown">
              <button class="ticket-sort-btn" @click.stop="toggleDropdown">
                <span class="ticket-sort-label">{{ ticketSortLabel }}</span>
                <svg
                  class="ticket-sort-chevron"
                  :class="{ 'ticket-sort-chevron--open': isDropdownOpen }"
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <g clip-path="url(#clip0_1255_25152)">
                    <path
                      d="M7.40148 9.95915C7.20157 10.2285 6.79843 10.2285 6.59852 9.95915L3.04574 5.17302C2.80084 4.8431 3.03633 4.375 3.44722 4.375L10.5528 4.375C10.9637 4.375 11.1992 4.8431 10.9543 5.17302L7.40148 9.95915Z"
                      fill="#333333"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_1255_25152">
                      <rect width="14" height="14" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
              <transition name="dropdown">
                <div v-if="isDropdownOpen" class="ticket-sort-menu">
                  <button
                    class="ticket-sort-item"
                    :class="{ 'ticket-sort-item--selected': ticketSortOption === 'all' }"
                    @click="changeTicketSortOption('all')"
                  >
                    구매 순
                  </button>
                  <button
                    class="ticket-sort-item"
                    :class="{ 'ticket-sort-item--selected': ticketSortOption === 'unused' }"
                    @click="changeTicketSortOption('unused')"
                  >
                    미사용보기
                  </button>
                  <button
                    class="ticket-sort-item"
                    :class="{ 'ticket-sort-item--selected': ticketSortOption === 'expiry' }"
                    @click="changeTicketSortOption('expiry')"
                  >
                    유효기간 순
                  </button>
                </div>
              </transition>
            </div>
            <div class="ticket-count-text">
              <span>사용 가능한 분석권</span>
              <span class="txt-red"> 총 {{ formatNumber(availableTicketCount) }}건</span>
            </div>
          </template>
          <!-- 모바일: 카운트 왼쪽, 드롭다운 오른쪽 -->
          <template v-else>
            <div class="ticket-count-text">
              <span>사용 가능한 분석권</span>
              <span class="txt-red"> 총 {{ formatNumber(availableTicketCount) }}건</span>
            </div>
            <div class="ticket-sort-dropdown">
              <button class="ticket-sort-btn" @click.stop="toggleDropdown">
                <span class="ticket-sort-label">{{ ticketSortLabel }}</span>
                <svg
                  class="ticket-sort-chevron"
                  :class="{ 'ticket-sort-chevron--open': isDropdownOpen }"
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <g clip-path="url(#clip0_1255_25152)">
                    <path
                      d="M7.40148 9.95915C7.20157 10.2285 6.79843 10.2285 6.59852 9.95915L3.04574 5.17302C2.80084 4.8431 3.03633 4.375 3.44722 4.375L10.5528 4.375C10.9637 4.375 11.1992 4.8431 10.9543 5.17302L7.40148 9.95915Z"
                      fill="#333333"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_1255_25152">
                      <rect width="14" height="14" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
              <transition name="dropdown">
                <div v-if="isDropdownOpen" class="ticket-sort-menu">
                  <button
                    class="ticket-sort-item"
                    :class="{ 'ticket-sort-item--selected': ticketSortOption === 'all' }"
                    @click="changeTicketSortOption('all')"
                  >
                    구매 순
                  </button>
                  <button
                    class="ticket-sort-item"
                    :class="{ 'ticket-sort-item--selected': ticketSortOption === 'unused' }"
                    @click="changeTicketSortOption('unused')"
                  >
                    미사용보기
                  </button>
                  <button
                    class="ticket-sort-item"
                    :class="{ 'ticket-sort-item--selected': ticketSortOption === 'expiry' }"
                    @click="changeTicketSortOption('expiry')"
                  >
                    유효기간 순
                  </button>
                </div>
              </transition>
            </div>
          </template>
        </div>
      </div>
      <!-- //TODO 있을 경우 -->
      <div class="" v-if="payOwnList?.length > 0">
        <!-- <div class="my-pay-tit-box gap-xxl">
          <div class="btn-box">
            <VBtn class="sBtn line h38" variant="outlined" @click="payList('PLAN_TICKET')"
              >월 이용권 상품 보기</VBtn
            >
            <VBtn class="sBtn h38" @click="payList('SUBSCRIPTION')">정기구독 상품 보기</VBtn> 
            <VBtn
              class="sBtn line h38"
              @click="goLink('/mypage/subscrRepoMonth')"
              variant="outlined"
              >월 이용권 상품 보기</VBtn
            >
            <VBtn class="sBtn h38" @click="goLink('/mypage/subscrRepo')">정기구독 상품 보기</VBtn> 
          </div>
        </div> -->
        <!-- 분석권 사용 내역은 별도 컴포넌트 사용 -->
        <div
          v-if="tabType === 'POINT_TICKET'"
          class="my-pay-list-box analysis-ticket-list-container"
        >
          <analysisTicketList :ticket-list="payOwnList" @item-click="handleTicketItemClick" />

          <!-- 분석권 상세 레이어 (리스트 컨테이너 내부에 absolute로 배치) -->
          <transition name="popup">
            <div
              v-if="showTicketDetailLayer && (selectedTicketId || selectedTicketIds)"
              class="ticket-detail-popup-content"
              :class="{ 'ticket-detail-popup-content--above': layerDirection === 'above' }"
              :style="{
                top: layerPosition.top ? layerPosition.top + 'px' : '70px',
              }"
              @click.stop
            >
              <AnalysisTicketDetailLayer
                :analysis-ticket-id="selectedTicketId"
                :analysis-ticket-ids="selectedTicketIds"
                @close="handleCloseTicketDetailLayer"
              />
            </div>
          </transition>
        </div>
        <!-- 포인트 내역은 기존 컴포넌트 사용 -->
        <div v-else class="my-pay-list-box">
          <subscrList
            :pr-pay-own-list="payOwnList"
            :tabType="tabType"
            :current-page="paramData.page"
          />
        </div>
        <Pagination
          v-if="totalElements > 0"
          :total-pages="totalPages"
          :current-page="paramData.page"
          :max-visible-pages="isWebView ? 5 : 10"
          @page-changed="changePage"
        />
      </div>
      <!-- //TODO 없을 경우 -->
      <div class="" v-else-if="!isListLoading">
        <div class="pay-none-point-box">
          <div class="txt1">이용 내역이 없습니다.</div>
          <button v-if="tabType === 'POINT_TICKET'" class="btn-go" @click="goToTicketPurchase()">
            분석권 구매하기
          </button>
          <button v-else class="btn-go" @click="chargePoints()">포인트 충전하기</button>
        </div>

        <!-- <div class="pay-none-box">
          <div class="none-title-box">
            <div class="icon-box">
              <IconSmile />
            </div>
            <div class="txt1">현재 이용중인 서비스가 없습니다.</div>
            <div class="txt2">상품을 구매하고 다양한 혜택을 누려보세요!</div>
          </div>
          <div class="btn-box">
            <VBtn
              class="sBtn line h38"
              @click="goLink('/mypage/subscrRepoMonth')"
              variant="outlined"
              >월 이용권 상품 보기</VBtn
            >
            <VBtn class="sBtn h38" @click="goLink('/mypage/subscrRepo')">정기구독 상품 보기</VBtn>
          </div>
          <div class="pay-line-box"></div>
          <div class="none-exp-box">
            <div class="tit-box">이용권 전용 혜택 💌</div>
            <div class="exp-dl-box">
              <dl>
                <dt><IconService01 /></dt>
                <dd>
                  <div class="tit">보안 저장소</div>
                  <div class="txt">
                    사주 데이터를 안전하게 보관하는 보안 저장소를 이용하세요! <br />데이터는 최신
                    암호화 기술로 보호됩니다.
                  </div>
                </dd>
              </dl>
              <dl>
                <dt><IconService02 /></dt>
                <dd>
                  <div class="tit">사주 저장</div>
                  <div class="txt">
                    요금제에 따라 기본 30건, 100건, 200건까지 저장할 수 있으며, <br />별도의 무제한
                    요금제를 통해 제한 없이 저장 가능합니다.
                  </div>
                </dd>
              </dl>
              <dl>
                <dt><IconService03 /></dt>
                <dd>
                  <div class="tit">그룹 관리</div>
                  <div class="txt">
                    그룹을 추가하고 순서를 자유롭게 변경할 수 있습니다. <br />즐겨찾기를 설정하여
                    원하는 그룹을 손쉽게 관리하세요!
                  </div>
                </dd>
              </dl>
              <dl>
                <dt><IconService04 /></dt>
                <dd>
                  <div class="tit">메모 작성 및 관리</div>
                  <div class="txt">
                    메모를 등록하고 효율적으로 사주관리를 해보세요! <br />요금제를 이용하면 무제한
                    메모 등록 가능해요! (기본 1개 제공)
                  </div>
                </dd>
              </dl>
              <dl>
                <dt><IconService05 /></dt>
                <dd>
                  <div class="tit">인쇄하기</div>
                  <div class="txt">
                    사주결과 출력물을 다양하게 선택해 인쇄할 수 있습니다!<br />
                    기본 A타입 외에 B,C,D 타입도 제공합니다. (유료)
                  </div>
                </dd>
              </dl>
              <dl>
                <dt><IconService06 /></dt>
                <dd>
                  <div class="tit">공유하기</div>
                  <div class="txt">
                    사주 내용을 텍스트로 복사하여 쉽게 공유할 수 있습니다!<br />
                    SNS(카카오톡)으로 간편하게 사주를 전송할 수 있습니다.
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div> -->
      </div>
    </div>
  </div>

  <!-- 모바일 하단 보유 포인트 footer -->
  <div v-if="isMobile && tabType && tabType.startsWith('POINT')" class="point-history__footer">
    <div class="point-history__footer-label">보유 포인트</div>
    <div class="point-history__footer-point">
      <div class="point-history__footer-point-icon"></div>
      <div class="point-history__footer-point-value">{{ paymentsStore.formattedPoints }} P</div>
    </div>
  </div>

  <Snackbar v-if="isMobile" ref="snackbarRef" />
</template>

<style lang="scss" scoped>
@media (max-width: 768px) {
  /* 모바일일 때 상단 여백 추가 (헤더 52px + 탭 영역 63px + safe-area-top) */
  .my-info-wrap {
    padding-top: calc(115px + var(--safe-area-top, 0px));
  }

  /* PC 버전 타이틀 박스 숨김 */
  .my-tit-box {
    display: none;
  }

  /* 분석권 사용 내역 탭 - 모바일 반응형 */
  .my-tab-st1-box {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 0;

    .btn-box {
      position: fixed;
      top: calc(52px + var(--safe-area-top, 0px));
      left: 0;
      width: 100%;
      height: fit-content;
      padding: 16px 0 16px 16px;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 6px;
      background: var(--gray-100-white, #fff);
      overflow-x: auto;
      overflow-y: hidden;
      white-space: nowrap;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      z-index: 9;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .ticket-sort-wrapper {
      position: fixed;
      top: calc(114px + var(--safe-area-top, 0px));
      left: 0;
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      margin-top: 0;
      background: var(--gray-100-white, #fff);
      border-bottom: 1px solid var(--Gray-500, #949494);
      z-index: 9;
      box-sizing: border-box;

      .ticket-count-text {
        font-family: Pretendard;
        font-size: 16px;
        font-weight: 500;
        line-height: 20px;
        color: var(--Gray-800, #333);

        .txt-red {
          color: var(--Orange-300-Main, #eb4c10);
        }
      }

      .ticket-sort-dropdown {
        .ticket-sort-btn {
          display: flex;
          align-items: center;
          gap: 2px;
          border: none;
          padding: 0;
          background: transparent;

          .ticket-sort-label {
            font-family: Pretendard;
            font-size: 14px;
            font-weight: 500;
            line-height: 18px;
            color: var(--Gray-700, #575757);
          }
        }

        .ticket-sort-chevron {
          width: 14px;
          height: 14px;
        }
      }
    }

    /* 충전내역, 포인트 이용내역 탭에서 보유 낭만 포인트 영역 숨김 */
    .point-txt-box {
      display: none;
    }
  }

  /* 분석권 리스트 - 정렬 영역 높이만큼 상단 여백 추가 */
  .analysis-ticket-list-container {
    padding-top: 44px;
  }

  /* 리스트 아이템 스타일 */
  :deep(.my-pay-list-box .row-box) {
    padding: 16px;
  }

  /* 분석권 상세 레이어 반응형 스타일 */
  .ticket-detail-popup-content {
    right: 10px !important;
    left: auto !important;
    max-width: 90% !important;
    min-width: 300px !important;
    width: auto !important;
  }

  /* 모바일 myinfo-box 스타일 조정 */
  .my-info-wrap .myinfo-box.com-bg-my-box.history {
    padding: 0;
    padding-bottom: 60px;
  }
}

/* 모바일 하단 포인트 footer */
.point-history__footer {
  display: none;
}

@media (max-width: 768px) {
  .point-history__footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: calc(50px + var(--safe-area-bottom, 0px));
    width: 100%;
    padding: 14px 16px var(--safe-area-bottom, 0px);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    background: var(--Gray-200, #f8f8f8);
    box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.2);
    z-index: 10;

    .point-history__footer-label {
      color: var(--Gray-800, #333);
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 22px;
    }

    .point-history__footer-point {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 8px;

      .point-history__footer-point-icon {
        display: flex;
        width: 20px;
        height: 20px;
        background: url('/src/assets/images_manse/point.svg') center center no-repeat;
        background-size: contain;
      }

      .point-history__footer-point-value {
        color: var(--orange-300-main, #eb4c10);
        font-family: Pretendard;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: 20px;
      }
    }
  }
}
</style>
